/* eslint-disable */
import { useState } from 'react'
import { Table, TableHeader, TableRow, TableBody, TableCell, TableHead } from '../ui/table'
import { ArrowDown, ArrowUp, MoreVerticalIcon, Plus } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Link, useNavigate } from 'react-router-dom'
import env from '../../env/env'
import { toCurrency } from '../../utils/CurrencyUtil'
import { get } from '../../http/restservice'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import {
  Menubar,
  MenubarArrow,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from '../ui/menubar'
import { Separator } from '../ui/separator'
import { sanitizeName } from '../../utils/Util'
import { EMPTY_STATE_ILLUSTRATIONS } from '../EmptyState/SIPsEmpty'
import PostHogTracker from '../../utils/PostHogTracker'
import useMediaQuery from '../../hooks/useMediaQuery'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog'
import { Sheet, SheetContent, SheetHeader, SheetDescription, SheetFooter } from '../ui/sheet'
import { Highlighter } from '../ui/highlighter'

const RecommendedFundPopup = ({
  isOpen,
  onClose,
  handleMFRedeem,
  selectedFund,
  recommendedFundLoading,
}) => {
  const isDesktop = useMediaQuery('(min-width: 1024px)')

  return (
    <>
      {isDesktop ? (
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className='text-center font-semibold text-xl font-DMSans'>
                Redemption
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className='text-start text-black-400 font-medium text-sm my-4'>
              {selectedFund?.holdingType ? (
                'Please contact your Relationship Manager (RM) to proceed with the redemption process.'
              ) : (
                <>
                  This is one of{' '}
                  <Highlighter action='underline' color='#FF9800'>
                    Fabits recommended funds{' '}
                  </Highlighter>{' '}
                  and may be linked to your financial goals. We suggest speaking with your wealth
                  advisor before proceeding with the redemption.
                  <br />
                  <br />
                  If you have already consulted your advisor, please continue.
                </>
              )}
            </DialogDescription>
            <DialogFooter className='mb-safe'>
              <Button
                onClick={onClose}
                className='w-full bg-error-500 text-white hover:bg-error-600'
              >
                Exit
              </Button>
              {!selectedFund?.holdingType && (
                <Button
                  loading={recommendedFundLoading}
                  onClick={() => handleMFRedeem(selectedFund)}
                  className='w-full bg-primary-500 text-white hover:bg-primary-600'
                >
                  Continue
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={isOpen} onOpenChange={onClose}>
          <SheetContent side='bottom' className='rounded-t-3xl font-DMSans'>
            <SheetHeader>
              <DialogTitle className='text-center font-semibold text-xl font-DMSans'>
                Redemption
              </DialogTitle>
            </SheetHeader>
            <SheetDescription className='text-start text-black-400 font-medium text-sm my-4'>
              {selectedFund?.holdingType ? (
                'Please contact your Relationship Manager (RM) to proceed with the redemption process.'
              ) : (
                <>
                  This is one of{' '}
                  <Highlighter action='underline' color='#FF9800'>
                    Fabits recommended funds{' '}
                  </Highlighter>{' '}
                  and may be linked to your financial goals. We suggest speaking with your wealth
                  advisor before proceeding with the redemption.
                  <br />
                  <br />
                  If you have already consulted your advisor, please continue.
                </>
              )}
            </SheetDescription>

            {!selectedFund?.holdingType && (
              <SheetFooter className='mb-safe'>
                {/* <Button onClick={onClose} className='w-full bg-primary-500 text-white hover:bg-primary-600'>Exit</Button> */}

                <Button
                  loading={recommendedFundLoading}
                  onClick={() => handleMFRedeem(selectedFund)}
                  className='w-full bg-primary-500 text-white hover:bg-primary-600'
                >
                  Continue
                </Button>
              </SheetFooter>
            )}
          </SheetContent>
        </Sheet>
      )}
    </>
  )
}

export default function MFIndividualInvestmentsTable({
  holdings,
  fabStarFunds,
  emptyStateVariant = 'funds',
}) {
  const fabitsFunds =
    fabStarFunds?.state === 'hasValue' &&
    fabStarFunds?.contents?.data?.length > 0 &&
    fabStarFunds?.contents?.data?.map((fund) => fund?.isin?.toLowerCase())

  const navigate = useNavigate()
  const [openFundMenu, setOpenFundMenu] = useState('')
  const [alertIsOpen, setAlertIsOpen] = useState(false)
  const [selectedFund, setSelectedFund] = useState(null)
  const [recommendedFundIsOpen, setRecommendedFundIsOpen] = useState(false)
  const [recommendedFundLoading, setRecommendedFundLoading] = useState(false)
  const emptyStateImage =
    EMPTY_STATE_ILLUSTRATIONS[emptyStateVariant] || EMPTY_STATE_ILLUSTRATIONS.funds

  const handleMFRedirect = async (fundId, fundName, currentValue) => {
    // Track individual holding clicked
    PostHogTracker.individualHoldingClicked(fundName || fundId, currentValue || 0)

    const response = await get(`/mutualfundservice/api/mfData/generalInfo/${fundId}`)

    if (response.status === 'SUCCESS') {
      navigate(`/dashboard/mutual-funds/details`, {
        state: {
          fundDetails: response?.data,
          schemeCode: fundId,
        },
      })
    }
  }

  const isFabitsRecommendedFund = (fund) =>
    Boolean(fabitsFunds && fabitsFunds?.includes(fund?.isincd?.toLowerCase()))

  const handleMFRedeem = async (fund) => {
    setRecommendedFundLoading(true)
    const response = await get(`/mutualfundservice/api/mfData/generalInfo/${fund?.schemeCode}`)

    if (response.status === 'SUCCESS') {
      navigate('/dashboard/mutual-funds/redeem/place-order', {
        state: {
          fundDetails: response?.data,
          fund,
          redirectBackTo: '/dashboard/mutual-funds/investments',
          redirectState: { tab: 'Mutual Funds' },
        },
      })
    }

    setRecommendedFundLoading(false)
  }

  const handleRedeemClick = (fund) => {
    if (fund?.holdingType) {
      setAlertIsOpen(true)
      return
    }

    if (isFabitsRecommendedFund(fund)) {
      setSelectedFund(fund)
      setRecommendedFundIsOpen(true)
      return
    }

    handleMFRedeem(fund)
  }

  const getReturns = (mfHolding) => {
    const total = mfHolding?.mainData?.reduce((sum, item) => sum + item.holding, 0)
    const invested = mfHolding?.mainData?.reduce((sum, item) => sum + item.netInvestedAmt, 0)

    // Calculate returns in percentage
    const returns = ((total - invested) / invested) * 100

    return returns < 0 ? toCurrency(returns) : toCurrency(returns)
  }

  const calculateParcentage = (invested, returns) => {
    const result = ((returns - invested) / invested) * 100
    return result
  }

  return (
    <>
      {/* Investment Details  */}
      <div className='px-4 lg:px-0'>
        <div className='mt-4'>
          {holdings?.mainData?.length > 0 && (
            <div className='mx-auto w-full rounded-lg border border-black-100 bg-white px-4 py-4 shadow-[0_8px_24px_rgba(4,3,19,0.05)] lg:max-w-2xl lg:px-6'>
              <div className='flex items-center justify-between gap-4'>
                <div className='min-w-0 flex-1 space-y-1'>
                  <p className='truncate text-base font-semibold text-primary-500 sm:text-lg'>
                    ₹{' '}
                    {toCurrency(
                      holdings?.mainData?.reduce((sum, item) => sum + item.netInvestedAmt, 0) || 0,
                    )}
                  </p>

                  <p className='text-sm text-black-400'>Invested</p>
                </div>
                <Separator orientation='vertical' className='h-11' />
                <div className='min-w-0 flex-1 space-y-1'>
                  <p className='truncate text-base font-semibold text-primary-500 sm:text-lg'>
                    ₹{' '}
                    {toCurrency(
                      holdings?.mainData?.reduce((sum, item) => sum + item.holding, 0) || 0,
                    )}
                  </p>
                  <p className='flex items-center gap-x-1 text-sm text-black-400'>
                    Current{' '}
                    <span
                      className={`${
                        getReturns(holdings) < 0
                          ? 'bg-error-600 text-error-600'
                          : 'bg-secondary-600 text-secondary-600'
                      } font-semibold  rounded-[50%] h-5 w-5 flex justify-center items-center mr-1 sm:hidden`}
                    >
                      {getReturns(holdings) < 0 ? (
                        <ArrowDown className='h-4 w-4 text-white' />
                      ) : (
                        <ArrowUp className='h-4 w-4 text-white' />
                      )}
                    </span>
                    <span
                      className={`${
                        getReturns(holdings) < 0 ? 'text-error-600' : 'text-secondary-600'
                      } font-semibold sm:hidden`}
                    >
                      {' '}
                      {getReturns(holdings)} %
                    </span>
                  </p>
                </div>

                <Separator orientation='vertical' className='h-11 hidden sm:block' />
                <div className='space-y-1 hidden sm:block flex-1'>
                  <p className='font-semibold text-lg'>
                    ₹{' '}
                    {toCurrency(
                      (holdings?.mainData?.reduce((sum, item) => sum + item.holding, 0) || 0) -
                        (holdings?.mainData?.reduce((sum, item) => sum + item.netInvestedAmt, 0) ||
                          0),
                    )}
                  </p>
                  <div className='flex gap-x-1 items-center text-sm text-black-400'>
                    Returns{' '}
                    <p className='font-semibold text-secondary-600 flex items-center'>
                      <span
                        className={`${
                          getReturns(holdings) < 0
                            ? 'bg-error-600 text-error-600'
                            : 'bg-secondary-600 text-secondary-600'
                        } rounded-[50%] h-5 w-5 flex justify-center items-center text-white mr-1`}
                      >
                        {getReturns(holdings) < 0 ? (
                          <ArrowDown className='h-4 w-3 text-white' />
                        ) : (
                          <ArrowUp className='h-4 w-3 text-white' />
                        )}
                      </span>
                      <span
                        className={`${
                          getReturns(holdings) < 0 ? 'text-error-600' : 'text-secondary-600'
                        } `}
                      >
                        {getReturns(holdings)} %
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='my-6 max-w-[1128px] mx-auto hidden md:block'>
        {holdings?.mainData?.length > 0 ? (
          <Table className='border-collapse rounded-lg shadow-lg'>
            <TableHeader className='bg-[#F4F7FE]'>
              <TableRow className='border-none'>
                <TableHead className='text-primary-400 py-3 h-0 w-10 rounded-l-xl'></TableHead>
                <TableHead className='text-primary-400 py-3 h-0 w-[60%]'>Fund Name</TableHead>
                <TableHead className='text-primary-400 py-3 h-0 px-4 text-right'>
                  Invested
                </TableHead>
                <TableHead className='text-primary-400 py-3 h-0 px-4 text-right'>Current</TableHead>
                <TableHead className='text-primary-400 py-3 pr-6 h-0 text-right rounded-r-xl'>
                  Returns
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {holdings?.mainData
                ?.filter((item) => Number(item.balance) > 0)
                ?.sort(
                  (a, b) =>
                    calculateParcentage(b.netInvestedAmt, b.holding) -
                    calculateParcentage(a.netInvestedAmt, a.holding),
                )
                ?.map((fund, index) => (
                  <TableRow
                    key={index}
                    className='cursor-pointer hover:shadow-[0px_24px_24px_-20px_rgba(0,0,0,0.08)]'
                    onClick={() => setOpenFundMenu(`desktop-${index}`)}
                  >
                    <TableCell className='p-0 w-12 pl-2'>
                      <img
                        src={`${env.MF_LOGOS_URL}/${fund.amcCode}.png`}
                        alt={fund.amcCode}
                        className='h-10 w-10 rounded-md m-auto'
                      />
                    </TableCell>
                    <TableCell>
                      <div className='w-full'>
                        <div className='flex items-center gap-x-2'>
                          <span className='font-semibold text-base leading-6'>
                            {sanitizeName(fund)}
                          </span>

                          {fabitsFunds && fabitsFunds?.includes(fund?.isincd?.toLowerCase()) && (
                            <span className='inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 inset-ring inset-ring-green-600/20'>
                              Fabits Recommended Fund
                            </span>
                          )}
                        </div>
                        <div className='text-xs leading-4 text-black-400 flex gap-x-2 items-center'>
                          <span> NAV ₹{fund?.closerate} </span>
                          <span>(Qty. {fund?.balance.toFixed(3)})</span>

                          {fund?.holdingType && (
                            <span
                              className='text-[10px] px-2 
                              m-0.5 text-white bg-secondary-500 rounded-full'
                            >
                              {fund?.holdingType}
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell className='px-4 py-4 text-base leading-6 font-semibold text-nowrap text-right'>
                      ₹ {toCurrency(fund?.netInvestedAmt)}
                    </TableCell>

                    <TableCell className='px-4 py-4 text-base leading-6 font-medium text-nowrap text-right'>
                      ₹ {toCurrency(fund?.holding)}
                    </TableCell>

                    <TableCell className='pr-6 py-4 font-semibold'>
                      <div className='flex items-center justify-end gap-x-2 min-w-40'>
                        <div className='flex flex-col justify-end items-end'>
                          <span className='text-base font-medium leading-6 text-nowrap'>
                            ₹ {toCurrency(fund?.holding - fund?.netInvestedAmt)}
                          </span>
                          <span
                            className={`${
                              calculateParcentage(fund.netInvestedAmt, fund.holding) === 0
                                ? 'text-gray-600'
                                : calculateParcentage(fund.netInvestedAmt, fund.holding) > 0
                                ? 'text-green-600'
                                : 'text-red-600'
                            } font-semibold text-xs leading-4`}
                          >
                            {calculateParcentage(fund.netInvestedAmt, fund.holding).toFixed(2) >
                              0 && '+'}
                            {calculateParcentage(fund.netInvestedAmt, fund.holding).toFixed(2)}%
                          </span>
                        </div>

                        <Menubar
                          value={openFundMenu === `desktop-${index}` ? 'actions' : ''}
                          onValueChange={(value) =>
                            setOpenFundMenu(value ? `desktop-${index}` : '')
                          }
                        >
                          <MenubarMenu value='actions'>
                            <MenubarTrigger
                              aria-label={`Actions for ${sanitizeName(fund)}`}
                              onClick={(e) => e.stopPropagation()}
                              className='hover:bg-[#F4F7FE] px-1 py-[10px] rounded-full h-10 w-7 flex justify-center items-center cursor-pointer'
                            >
                              <MoreVerticalIcon className='h-4' />
                            </MenubarTrigger>

                            <MenubarContent
                              onClick={(e) => e.stopPropagation()}
                              side='left'
                              sideOffset={4}
                              alignOffset={8}
                              className='mt-0 overflow-visible rounded-[10px] flex flex-col gap-y-1'
                            >
                              <MenubarArrow className='h-2.5 w-4' />
                              <MenubarItem
                                className='pl-2 text-sm text-black-500 cursor-pointer hover:bg-blue-100'
                                onSelect={() => handleRedeemClick(fund)}
                              >
                                Redeem
                              </MenubarItem>
                              <MenubarItem
                                className='pl-2 text-sm text-black-500 cursor-pointer hover:bg-blue-100'
                                onSelect={() =>
                                  handleMFRedirect(
                                    fund?.schemeCode,
                                    sanitizeName(fund),
                                    fund?.holding,
                                  )
                                }
                              >
                                View details
                              </MenubarItem>
                            </MenubarContent>
                          </MenubarMenu>
                        </Menubar>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        ) : (
          <div className='bg-background flex items-center justify-center p-4'>
            <Card className='w-full max-w-2xl text-center border-none shadow-none'>
              <CardHeader className='flex flex-col justify-center items-center space-y-2'>
                <img
                  className='mx-auto h-auto w-[220px] max-w-[64vw] sm:w-[240px]'
                  src={emptyStateImage}
                  alt=''
                  aria-hidden='true'
                  draggable='false'
                />
                <CardTitle className='text-xl'>You have not made an investment yet</CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-muted-foreground mb-4'>
                  Start building your investment portfolio by placing your first mutual fund order.
                </p>
                <Link to='/dashboard/mutual-funds'>
                  <Button className='w-full'>
                    <Plus className='mr-2 h-4 w-4' /> Invest Now
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <div className='mt-4 max-w-[1128px] mx-auto px-2 md:hidden'>
        {holdings?.mainData?.length > 0 ? (
          <Table className='w-full table-auto border-collapse rounded-lg shadow-lg'>
            <TableBody>
              {holdings?.mainData
                ?.filter((item) => Number(item.balance) > 0)
                ?.sort(
                  (a, b) =>
                    calculateParcentage(b.netInvestedAmt, b.holding) -
                    calculateParcentage(a.netInvestedAmt, a.holding),
                )
                .map((fund, index) => (
                  <TableRow
                    key={index}
                    className='cursor-pointer hover:shadow-[0px_24px_24px_-20px_rgba(0,0,0,0.08)]'
                    onClick={() => setOpenFundMenu(`mobile-${index}`)}
                  >
                    <TableCell className='p-0 w-10'>
                      <img
                        src={`${env.MF_LOGOS_URL}/${fund.amcCode}.png`}
                        alt={`logos`}
                        className='h-10 w-10'
                      />
                    </TableCell>
                    <TableCell className='px-4 py-4 flex items-center'>
                      <div className='w-full'>
                        <div className='flex justify-between items-center gap-x-2'>
                          <div>
                            <h3 className='font-semibold'>
                              <span> {sanitizeName(fund)}</span>

                              {fund?.holdingType && (
                                <span
                                  className='text-xs px-2 
                              m-0.5 text-white bg-secondary-500 rounded-full'
                                >
                                  {fund?.holdingType}
                                </span>
                              )}
                            </h3>
                          </div>

                          <div className='flex items-center gap-x-1'>
                            <Menubar
                              value={openFundMenu === `mobile-${index}` ? 'actions' : ''}
                              onValueChange={(value) =>
                                setOpenFundMenu(value ? `mobile-${index}` : '')
                              }
                            >
                              <MenubarMenu value='actions'>
                                <MenubarTrigger
                                  aria-label={`Actions for ${sanitizeName(fund)}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className='md:hidden hover:bg-[#F4F7FE] px-1 py-[10px] rounded-full h-6 w-6 flex justify-center items-center cursor-pointer'
                                >
                                  <MoreVerticalIcon className='h-4' />
                                </MenubarTrigger>

                                <MenubarContent
                                  onClick={(e) => e.stopPropagation()}
                                  sideOffset={4}
                                  className='mt-0 overflow-visible rounded-[10px] flex flex-col gap-y-1'
                                >
                                  <MenubarArrow className='h-2.5 w-4' />
                                  <MenubarItem
                                    className='pl-2 text-sm text-black-500 cursor-pointer hover:bg-blue-100'
                                    onSelect={() => handleRedeemClick(fund)}
                                  >
                                    <div>Redeem</div>
                                  </MenubarItem>
                                  <MenubarItem
                                    className='pl-2 text-sm text-black-500 cursor-pointer hover:bg-blue-100'
                                    onSelect={() =>
                                      handleMFRedirect(
                                        fund?.schemeCode,
                                        sanitizeName(fund),
                                        fund?.holding,
                                      )
                                    }
                                  >
                                    View details
                                  </MenubarItem>
                                </MenubarContent>
                              </MenubarMenu>
                            </Menubar>
                          </div>
                        </div>
                        {/* 
                        {fabitsFunds && fabitsFunds?.includes(fund?.isincd?.toLowerCase()) && <p className='inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 inset-ring inset-ring-green-600/20'>Fabits Recommended Fund</p>} */}

                        <div className='flex justify-between items-center w-full text-xs space-x-2 text-black-400'>
                          <p className='space-x-2'>
                            <span>NAV ₹{toCurrency(fund?.closerate)}</span>
                            <span>(Qty. {fund?.balance?.toFixed(3)})</span>
                          </p>

                          <span
                            className={`${
                              calculateParcentage(fund.netInvestedAmt, fund.holding) === 0
                                ? 'text-gray-600'
                                : calculateParcentage(fund.netInvestedAmt, fund.holding) > 0
                                ? 'text-green-600'
                                : 'text-red-600'
                            } font-semibold text-xs leading-4`}
                          >
                            (
                            {calculateParcentage(fund.netInvestedAmt, fund.holding).toFixed(2) >
                              0 && '+'}
                            {calculateParcentage(fund.netInvestedAmt, fund.holding).toFixed(2)}
                            %)
                          </span>
                        </div>

                        <div className='flex justify-between items-center lg:block mt-[2px]'>
                          <div className='w-full flex gap-x-2 justify-between items-center md:hidden'>
                            <span className='font-semibold text-nowrap'>
                              ₹ {toCurrency(fund.netInvestedAmt)}
                            </span>
                            <p className='font-semibold space-x-2 text-nowrap'>
                              <span> ₹ {toCurrency(fund.holding)}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        ) : (
          <div className='bg-background flex items-center justify-center'>
            <Card className='w-full max-w-2xl text-center border-none shadow-none'>
              <CardHeader className='flex flex-col justify-center items-center space-y-2'>
                <img
                  className='mx-auto h-auto w-[220px] max-w-[64vw] sm:w-[240px]'
                  src={emptyStateImage}
                  alt=''
                  aria-hidden='true'
                  draggable='false'
                />
                <CardTitle className='text-xl'>You have not made an investment yet</CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-muted-foreground mb-4'>
                  Start building your investment portfolio by placing your first mutual fund order.
                </p>
                <Link to='/dashboard/mutual-funds'>
                  <Button className='w-full'>
                    <Plus className='mr-2 h-4 w-4' /> Invest Now
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <RecommendedFundPopup
        isOpen={recommendedFundIsOpen}
        onClose={() => setRecommendedFundIsOpen(false)}
        handleMFRedeem={handleMFRedeem}
        selectedFund={selectedFund}
        recommendedFundLoading={recommendedFundLoading}
      />

      <AlertDialog open={alertIsOpen} onOpenChange={setAlertIsOpen}>
        <AlertDialogContent className='font-DMSans'>
          <AlertDialogHeader>
            <AlertDialogTitle>Redemption</AlertDialogTitle>
            <AlertDialogDescription>
              Please contact your Relationship Manager (RM) to proceed with the redemption process.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
